import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'fr', flagUrl: 'https://flagcdn.com/w20/fr.png', alt: 'France' },
  { code: 'en', flagUrl: 'https://flagcdn.com/w20/gb.png', alt: 'United Kingdom' },
  { code: 'es', flagUrl: 'https://flagcdn.com/w20/es.png', alt: 'Spain' },
  { code: 'zh', flagUrl: 'https://flagcdn.com/w20/cn.png', alt: 'China' },
  { code: 'ar', flagUrl: 'https://flagcdn.com/w20/sa.png', alt: 'Saudi Arabia' },
];

export default function LanguageSelector() {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setIsOpen(false);
    // Handle HTML dir attribute for RTL
    if (code === 'ar') {
      document.documentElement.setAttribute('dir', 'rtl');
      document.documentElement.setAttribute('lang', 'ar');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', code);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Set initial direction
  useEffect(() => {
    if (i18n.language === 'ar') {
      document.documentElement.setAttribute('dir', 'rtl');
      document.documentElement.setAttribute('lang', 'ar');
    }
  }, [i18n.language]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap border border-background-200/70"
        aria-label={t('langSelector.selectLanguage', 'Select language')}
      >
        <img
          src={currentLang.flagUrl}
          alt={currentLang.alt}
          className="w-5 h-4 object-cover rounded-sm"
        />
        <span className="hidden sm:inline">{t(`langSelector.${currentLang.code}`, currentLang.code.toUpperCase())}</span>
        <i className={isOpen ? 'ri-arrow-up-s-line text-xs w-3 h-3 flex items-center justify-center' : 'ri-arrow-down-s-line text-xs w-3 h-3 flex items-center justify-center'}></i>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-lg border border-background-200/70 py-2 z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`flex items-center gap-3 w-full px-4 py-2.5 text-sm transition-colors cursor-pointer whitespace-nowrap text-left ${
                i18n.language === lang.code
                  ? 'bg-primary-50 text-primary-700 font-semibold'
                  : 'text-foreground-600 hover:bg-background-100'
              }`}
            >
              <img
                src={lang.flagUrl}
                alt={lang.alt}
                className="w-5 h-4 object-cover rounded-sm"
              />
              <span>{t(`langSelector.${lang.code}`, lang.code.toUpperCase())}</span>
              {i18n.language === lang.code && (
                <i className="ri-check-line text-primary-500 ml-auto w-4 h-4 flex items-center justify-center"></i>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}